// components/errors/NetworkErrorPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { WifiOff, RefreshCw, Home, Wifi, Globe, Signal, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlassCard, GlassButton, GlassAlert } from '../ui/GlassComponents';

const NetworkErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('errors');
  const { t: tCommon } = useTranslation('common');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkConnection = async () => {
    setIsChecking(true);
    try {
      // Try to fetch a small resource to check connectivity
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache'
      });
      if (response.ok) {
        window.location.reload();
      }
    } catch (error) {
      // Still offline
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Network pattern overlay */}
      <div className="fixed inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 50% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)
            `,
          }}
        />
      </div>

      <div className="relative z-10 max-w-2xl w-full space-y-6">
        {/* Main network error card */}
        <GlassCard className="backdrop-blur-xl">
          <div className="text-center space-y-6 p-8">
            {/* Icon with status indicator */}
            <div className="relative mx-auto w-24 h-24">
              {isOnline ? (
                <div className="relative bg-emerald-500/10 backdrop-blur-sm border border-emerald-500/30 rounded-full p-6 flex items-center justify-center">
                  <Wifi className="h-12 w-12 text-emerald-400" />
                </div>
              ) : (
                <>
                  <div className="absolute inset-0 bg-gray-500/20 rounded-full animate-ping" />
                  <div className="relative bg-gray-500/10 backdrop-blur-sm border border-gray-500/30 rounded-full p-6 flex items-center justify-center">
                    <WifiOff className="h-12 w-12 text-gray-400" />
                  </div>
                </>
              )}
            </div>

            {/* Error title */}
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {isOnline ? t('network.reconnected_title') : t('network.offline_title')}
              </h1>
              <p className="text-lg text-white/60">
                {isOnline ? t('network.reconnected_subtitle') : t('network.offline_subtitle')}
              </p>
            </div>

            {/* Status message */}
            <GlassAlert variant={isOnline ? "success" : "warning"} className="text-start">
              <div className="space-y-2">
                <p className="text-white/90 font-medium flex items-center gap-2">
                  {isOnline ? <CheckCircle className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
                  {isOnline ? t('network.status_online') : t('network.status_offline')}
                </p>
                <p className="text-white/70 text-sm">
                  {isOnline ? t('network.online_message') : t('network.offline_message')}
                </p>
              </div>
            </GlassAlert>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <GlassButton
                variant="primary"
                size="lg"
                onClick={checkConnection}
                disabled={isChecking}
                className="group"
              >
                <RefreshCw className={`h-5 w-5 me-2 ${isChecking ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                {isChecking ? t('network.checking') : t('network.check_connection_btn')}
              </GlassButton>

              <GlassButton
                variant="secondary"
                size="lg"
                onClick={() => navigate('/')}
                className="group"
              >
                <Home className="h-5 w-5 me-2 group-hover:scale-110 transition-transform" />
                {tCommon('actions.home')}
              </GlassButton>
            </div>
          </div>
        </GlassCard>

        {/* Connection status card */}
        <GlassCard className="backdrop-blur-md">
          <div className="p-6 space-y-4">
            <h3 className="text-white/90 font-semibold flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-400" />
              {t('network.connection_status_title')}
            </h3>

            <div className="space-y-3">
              {/* Network status */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <span className="text-white/70 text-sm flex items-center gap-2">
                  <Signal className="h-4 w-4" />
                  {t('network.network_status_label')}
                </span>
                <span className={`flex items-center gap-2 text-sm ${isOnline ? 'text-emerald-400' : 'text-gray-400'}`}>
                  <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-gray-400 animate-pulse'}`} />
                  {isOnline ? t('network.online_status') : t('network.offline_status')}
                </span>
              </div>

              {/* Troubleshooting tips */}
              <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="text-white/80 font-medium text-sm mb-2">{t('network.troubleshooting_title')}</p>
                <ul className="space-y-1 text-white/60 text-xs">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400/60 mt-0.5">•</span>
                    <span>{t('network.tip_wifi')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400/60 mt-0.5">•</span>
                    <span>{t('network.tip_router')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400/60 mt-0.5">•</span>
                    <span>{t('network.tip_firewall')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400/60 mt-0.5">•</span>
                    <span>{t('network.tip_admin')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default NetworkErrorPage;
